import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, Mail, Trash2, AlertTriangle, BellRing } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/constants';
import BottomNav from '../components/BottomNav';
import { disablePushNotifications, enablePushNotifications, getPushStatus, sendPushTest, supportsPushNotifications } from '../lib/pushNotifications';

const SettingsPage = () => {
  const navigate = useNavigate();
  const { token, user, updateUser, logout } = useAuth();
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingResetEmail, setSendingResetEmail] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);

  useEffect(() => {
    if (user) {
      setUsername(user.username || '');
    }
  }, [user]);

  useEffect(() => {
    if (!token) return;
    setPushSupported(supportsPushNotifications());
    getPushStatus(token)
      .then((status) => setPushEnabled(status.enabled))
      .catch(() => setPushEnabled(false));
  }, [token]);

  const handlePushToggle = async (enabled) => {
    if (!pushSupported) {
      toast.error('Push notifications require a supported browser over HTTPS.');
      return;
    }
    setPushLoading(true);
    try {
      if (enabled) {
        await enablePushNotifications(token);
        toast.success('Push notifications are on');
      } else {
        await disablePushNotifications(token);
        toast.success('Push notifications are off');
      }
      setPushEnabled(enabled);
    } catch (error) {
      toast.error(error.message || 'Unable to update push notifications');
    } finally {
      setPushLoading(false);
    }
  };

  const handlePushTest = async () => {
    setPushLoading(true);
    try {
      await sendPushTest(token);
      toast.success('Test push notification sent');
    } catch (error) {
      toast.error('Unable to send a test push notification');
    } finally {
      setPushLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    if (username === user.username) {
      toast.info('No changes to save');
      return;
    }

    setLoading(true);
    try {
      const payload = { username };

      const res = await axios.put(`${API}/profile/update`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Profile updated successfully!');
      
      // Update local user data with the new username
      if (res.data.user) {
        updateUser(res.data.user);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!user?.email) {
      toast.error('No email associated with this account');
      return;
    }

    setSendingResetEmail(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, {
        email: user.email
      });
      toast.success('Password reset email sent! Check your inbox.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setSendingResetEmail(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword.trim()) {
      toast.error('Please enter your password to confirm');
      return;
    }
    
    setDeletingAccount(true);
    try {
      await axios.post(`${API}/auth/delete-account`, {
        password: deletePassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Your account has been deleted');
      logout();
      navigate('/auth');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Incorrect password');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to delete account');
      }
    } finally {
      setDeletingAccount(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0B0B0C] flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/70 mb-4">Please sign in to access settings</p>
          <Button onClick={() => navigate('/auth')} className="bg-amber-600">Sign In</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0C] flex flex-col">
      {/* Header */}
      <div className="flex items-center p-4 border-b border-white/10">
        <button onClick={() => navigate(-1)} className="text-white mr-4">
          <X size={24} />
        </button>
        <h1 className="playfair text-xl text-white flex-1 text-center pr-8">Settings</h1>
      </div>

      {/* Content */}
      <div className="flex-1 p-6 pb-24">
        {/* Username Section */}
        <div className="mb-8">
          <h2 className="text-white font-bold mb-4">Profile Name</h2>
          <Input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Username"
            className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
          />
        </div>

        {/* Save Username Button */}
        <Button 
          onClick={handleUpdateProfile}
          disabled={loading}
          className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold py-6 rounded-full mb-8"
        >
          {loading ? 'Saving...' : 'Save Username'}
        </Button>

        <div className="mb-8 border-t border-white/10 pt-8">
          <div className="flex items-start justify-between gap-6">
            <div>
              <div className="flex items-center gap-2">
                <BellRing size={18} className="text-amber-500" />
                <h2 className="text-white font-bold">Push Notifications</h2>
              </div>
              <p data-testid="push-notifications-description" className="text-white/50 text-sm mt-2">
                Get phone alerts for comments, ratings, favorites, and account activity.
              </p>
            </div>
            <Switch
              data-testid="push-notifications-toggle"
              aria-label="Toggle push notifications"
              checked={pushEnabled}
              disabled={pushLoading || !pushSupported}
              onCheckedChange={handlePushToggle}
              className="data-[state=checked]:bg-amber-600"
            />
          </div>
          {!pushSupported && (
            <p data-testid="push-notifications-unsupported" className="text-white/35 text-xs mt-3">
              Push alerts are available when Cigar Ranker is installed in a supported browser over HTTPS.
            </p>
          )}
          {pushEnabled && (
            <Button
              data-testid="push-notifications-test-button"
              onClick={handlePushTest}
              disabled={pushLoading}
              variant="outline"
              className="mt-4 border-amber-500/50 text-amber-500 hover:bg-amber-500/10"
            >
              Send a test notification
            </Button>
          )}
        </div>

        {/* Password Section */}
        <div className="mb-8">
          <h2 className="text-white font-bold mb-2">Change Password</h2>
          <p className="text-white/50 text-sm mb-4">
            For security, password changes are done via email verification. Click the button below and we&apos;ll send a reset link to your email.
          </p>
          <Button 
            onClick={handleChangePassword}
            disabled={sendingResetEmail}
            variant="outline"
            className="w-full border-amber-500/50 text-amber-500 hover:bg-amber-500/10 py-6 rounded-full flex items-center justify-center gap-2"
          >
            <Mail size={20} />
            {sendingResetEmail ? 'Sending...' : 'Send Password Reset Email'}
          </Button>
        </div>

        {/* Delete Account Section */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <h2 className="text-red-500 font-bold mb-2">Danger Zone</h2>
          <p className="text-white/50 text-sm mb-4">
            Permanently delete your account and all associated data. This action cannot be undone.
          </p>
          <button
            data-testid="delete-account-button"
            onClick={() => setShowDeleteModal(true)}
            className="w-full text-red-400 hover:text-red-300 text-sm py-3 flex items-center justify-center gap-2 border border-red-500/30 rounded-full hover:bg-red-500/10 transition-colors"
          >
            <Trash2 size={18} />
            Delete my account
          </button>
        </div>
      </div>
      
      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && setShowDeleteModal(false)}
        >
          <div className="bg-[#1a1a1c] border border-red-500/30 rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-red-500 flex items-center gap-2">
                <AlertTriangle size={24} />
                Delete Account
              </h2>
              <button onClick={() => setShowDeleteModal(false)} className="text-white/50 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-4">
              <p className="text-red-400 text-sm font-medium mb-2">Warning: This action is permanent!</p>
              <p className="text-white/60 text-sm">
                Deleting your account will permanently remove:
              </p>
              <ul className="text-white/50 text-sm mt-2 space-y-1 list-disc list-inside">
                <li>Your profile and account info</li>
                <li>All your ratings and reviews</li>
                <li>All your comments and notes</li>
                <li>Your favorites list</li>
                <li>Your uploaded photos</li>
              </ul>
            </div>
            
            <div className="mb-4">
              <label className="text-white/70 text-sm mb-2 block">Enter your password to confirm</label>
              <input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Your password"
                className="w-full bg-white/5 border border-white/10 text-white rounded-lg py-2 px-3 placeholder:text-white/30"
              />
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={() => setShowDeleteModal(false)}
                variant="outline"
                className="flex-1 border-white/20 text-white hover:bg-white/10 rounded-full py-3"
              >
                Cancel
              </Button>
              <Button
                data-testid="confirm-delete-account-button"
                onClick={handleDeleteAccount}
                disabled={deletingAccount || !deletePassword.trim()}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 rounded-full disabled:opacity-50"
              >
                {deletingAccount ? 'Deleting...' : 'Delete Account'}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      <BottomNav />
    </div>
  );
};

export default SettingsPage;
